import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'shared/i18n'
import { Menu, X, Globe } from 'lucide-react'
import { getCustomerToken } from 'shared/store/authStore'
import { unifiedAuth } from '../../api/authClient'
import { Modal } from './Modal'
import UnifiedLoginForm from '../auth/UnifiedLoginForm'
import UnifiedRegisterForm from '../auth/UnifiedRegisterForm'
import CompanyRegisterModalContent from '../auth/CompanyRegisterModalContent'
import UserMenu from '../UserMenu'
import { useCompanyRegisterModalStore } from '../../store/companyRegisterModalStore'
import { usePaymentModalStore } from '../../store/paymentModalStore'

interface NavigationProps {
  onScrollToSection?: (sectionId: string) => void
}

export const Navigation: React.FC<NavigationProps> = ({ onScrollToSection }) => {
  const { t } = useTranslation('portal')
  const location = useLocation()
  const isLanding = location.pathname === '/'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [isScrolled, setIsScrolled] = useState(false)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [registerModalOpen, setRegisterModalOpen] = useState(false)
  const companyRegisterModalOpen = useCompanyRegisterModalStore((s) => s.open)
  const closeCompanyRegisterModal = useCompanyRegisterModalStore((s) => s.closeModal)
  const openCompanyRegisterModal = useCompanyRegisterModalStore((s) => s.openModal)
  const openPaymentModal = usePaymentModalStore((s) => s.openModal)

  const menuItems = [
    { id: 'home', label: t('nav.home'), to: '/' },
    { id: 'pay-invoice', label: t('nav.payInvoice'), openPaymentModal: true },
    { id: 'for-companies', label: t('nav.forCompanies'), to: '/#for-companies' },
    { id: 'about', label: t('nav.about'), to: '/#about' },
    { id: 'contact', label: t('nav.contact'), to: '/#contact' },
  ]

  const languages = [
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
    { code: 'sr', name: 'Srpski', flag: '🇷🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ]

  useEffect(() => {
    if (location.pathname === '/dashboard') setActiveSection('home')
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
      if (!isLanding) return
      for (const item of menuItems) {
        const element = document.getElementById(item.id)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(item.id)
            break
          }
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLanding])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (isMobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.hamburger-btn')) setIsMobileMenuOpen(false)
      if (isLanguageMenuOpen && !target.closest('.language-menu')) setIsLanguageMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen, isLanguageMenuOpen])

  const scrollToSection = (sectionId: string, event?: React.MouseEvent) => {
    event?.preventDefault()
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 80
      const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      setActiveSection(sectionId)
      setIsMobileMenuOpen(false)
    }
    onScrollToSection?.(sectionId)
  }

  const handleNavClick = (item: (typeof menuItems)[0], e: React.MouseEvent) => {
    if (item.to?.startsWith('/#')) {
      const sectionId = item.to.replace('/#', '')
      if (isLanding) {
        e.preventDefault()
        scrollToSection(sectionId, e)
      }
    }
  }

  const navItemClass = (itemId: string) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeSection === itemId ? 'text-blue-600 bg-blue-50' : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'}`
  const mobileNavItemClass = (itemId: string) =>
    `block w-full max-w-xs px-4 py-3 rounded-lg text-base font-medium transition-colors ${activeSection === itemId ? 'text-blue-600 bg-blue-50' : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'}`

  /** Frontend uses bs, backend uses bhs for Bosnian. */
  const frontendToBackendLocale = (code: string) => (code === 'bs' ? 'bhs' : code)

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode)
    setIsLanguageMenuOpen(false)
    setIsMobileMenuOpen(false)
    if (getCustomerToken()) {
      unifiedAuth.patchMe({ preferred_locale: frontendToBackendLocale(langCode) }).catch(() => {})
    }
  }

  const currentLng = (i18n.language || 'bs').split('-')[0]
  const currentLanguage = languages.find((lang) => lang.code === currentLng) || languages[0]

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/90 backdrop-blur-sm'} border-b border-slate-200`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
              FiskalPay
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-1">
            {menuItems.map((item) =>
              'openPaymentModal' in item && item.openPaymentModal ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openPaymentModal()}
                  className={navItemClass(item.id)}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.to!}
                  onClick={(e) => handleNavClick(item, e)}
                  className={navItemClass(item.id)}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div className="relative language-menu">
              <button data-testid="nav-language-toggle" onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-md transition-colors" aria-label="Change language">
                <Globe className="w-4 h-4" />
                <span className="text-base">{currentLanguage.flag}</span>
                <span>{currentLanguage.code.toUpperCase()}</span>
              </button>
              {isLanguageMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  {languages.map((lang) => (
                    <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${currentLng === lang.code ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}>
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {currentLng === lang.code && <span className="ml-auto text-blue-600">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <UserMenu
              onOpenLogin={() => setLoginModalOpen(true)}
              onOpenRegister={() => setRegisterModalOpen(true)}
              onOpenRegisterCompany={openCompanyRegisterModal}
              variant="light"
            />
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden hamburger-btn p-2 rounded-md text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && <div className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setIsMobileMenuOpen(false)} />}
      <div className={`md:hidden mobile-menu fixed top-16 right-0 h-[calc(100vh-4rem)] w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex-1 px-4 py-6 space-y-2 overflow-y-auto flex flex-col items-center text-center">
            {menuItems.map((item) =>
              'openPaymentModal' in item && item.openPaymentModal ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { openPaymentModal(); setIsMobileMenuOpen(false) }}
                  className={mobileNavItemClass(item.id)}
                >
                  {item.label}
                </button>
              ) : (
                <Link key={item.id} to={item.to!} onClick={(e) => handleNavClick(item, e)} className={mobileNavItemClass(item.id)}>
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-4 border-t border-slate-200 w-full flex flex-col items-center">
              <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('nav.language')}</div>
              {languages.map((lang) => (
                <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full max-w-xs flex items-center justify-center gap-3 px-4 py-3 text-base rounded-lg transition-colors ${currentLng === lang.code ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}>
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {currentLng === lang.code && <span className="ml-1 text-blue-600">✓</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 border-t border-slate-200 w-full">
            <UserMenu
              layout="inline"
              onOpenLogin={() => { setLoginModalOpen(true); setIsMobileMenuOpen(false) }}
              onOpenRegister={() => { setRegisterModalOpen(true); setIsMobileMenuOpen(false) }}
              onOpenRegisterCompany={() => { openCompanyRegisterModal(); setIsMobileMenuOpen(false) }}
              variant="light"
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        title={t('auth.login.title')}
        size="sm"
        showCloseButton={true}
      >
        <div data-testid="modal-login" className="bg-white rounded-xl border border-slate-200 p-4">
          <UnifiedLoginForm
            embedded
            onClose={() => setLoginModalOpen(false)}
            onOpenRegister={() => { setLoginModalOpen(false); setRegisterModalOpen(true) }}
          />
        </div>
      </Modal>
      <Modal
        isOpen={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        title={t('auth.register.title')}
        size="md"
        showCloseButton={true}
      >
        <div data-testid="modal-register" className="bg-white rounded-xl border border-slate-200 p-4">
          <UnifiedRegisterForm
            embedded
            onClose={() => setRegisterModalOpen(false)}
            onOpenLogin={() => { setRegisterModalOpen(false); setLoginModalOpen(true) }}
          />
        </div>
      </Modal>
      <Modal
        isOpen={companyRegisterModalOpen}
        onClose={closeCompanyRegisterModal}
        title={t('landing.companySection.modalTitle')}
        size="md"
        showCloseButton={true}
      >
        <div data-testid="modal-company-register" className="bg-white rounded-xl border border-slate-200 p-4">
          <CompanyRegisterModalContent
            onClose={closeCompanyRegisterModal}
            onOpenLogin={() => { closeCompanyRegisterModal(); setLoginModalOpen(true) }}
            onSuccess={closeCompanyRegisterModal}
          />
        </div>
      </Modal>
    </nav>
  )
}

export default Navigation
